import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ActionDropdown, ActionItem } from '../Table/TableDataView';
import usePatientManagementActions from '../../hooks/usePatientManagementActions';
import ManagePatientContextProvider from '../../lib/context/ManagePatientContext';
import useApi from '../../hooks/useApi';

type PatientActionMenuProps = {
  patientId: string;
  onNewCaseCreated?: (caseId: number) => Promise<void>;
  extraActions?: ActionItem[];
};

export const PatientActionMenu = ({ patientId, extraActions, onNewCaseCreated }: PatientActionMenuProps) => {
  const queryClient = useQueryClient();
  const { Api } = useApi();

  const { data: patientDetails } = useQuery({
    queryKey: ['patientDetails', patientId],
    queryFn: async () => {
      const response = await Api.referrers.getPatient(patientId);

      return response?.data?.users[0] || null;
    },
    enabled: !!patientId
  });

  const { generatePatientActions } = usePatientManagementActions({
    refetchFn: (params) => {
      if (params?.createdCaseId) {
        onNewCaseCreated?.(params.createdCaseId);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['patientDetails'] }).then();
    }
  });

  if (!patientDetails) return null;

  const patientActions = generatePatientActions(patientDetails);

  const mergedActions = extraActions?.length ? [...patientActions, ...extraActions] : patientActions;

  return (
    <ActionDropdown customDataTestID="timeline-patient-action-dropdown" actionItems={mergedActions} rowData={{}} />
  );
};

const ActionMenuWithContextProvider = (props: PatientActionMenuProps) => {
  return (
    <ManagePatientContextProvider>
      <PatientActionMenu {...props} />
    </ManagePatientContextProvider>
  );
};

export default ActionMenuWithContextProvider;
